import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { useCan } from "@/shared/auth";
import { PageHeader } from "@/shared/components/PageHeader";
import { toast } from "@/shared/lib/toast";
import { Button } from "@/shared/ui/button";

import { useBulkRetryIngest } from "../api/use-bulk-retry-ingest";
import { useReviewQueue } from "../api/use-review-queue";
import { ReviewQueueTable } from "../components/ReviewQueueTable";

function ReviewQueueSkeleton(): JSX.Element {
  const { t } = useTranslation("manual_review");
  return (
    <div className="space-y-2 p-4" data-testid="manual-review-loading">
      <span className="sr-only">{t("loading")}</span>
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="h-[var(--row-height-default)] animate-pulse rounded-[var(--r)] bg-[var(--surface-sunken)]"
        />
      ))}
    </div>
  );
}

export function ManualReviewPage(): JSX.Element {
  const { t } = useTranslation("manual_review");
  const { data: rows, isLoading, isError } = useReviewQueue();

  const canWrite = useCan("orders:write");
  const canResolve = useCan("stock:write");
  const canRowResolve = canWrite && canResolve;

  const bulkRetry = useBulkRetryIngest();

  const queueEmpty = !rows || rows.length === 0;
  const bulkDisabled = bulkRetry.isPending || isLoading || queueEmpty;

  function handleBulkRetry(): void {
    bulkRetry.mutate(undefined, {
      onSuccess: (result) => {
        if (result.attempted === 0) {
          toast.info(t("toast_bulk_empty"));
        } else {
          toast.success(
            t("toast_bulk_done", {
              attempted: result.attempted,
              locked: result.locked,
              stillHeld: result.still_held,
            }),
          );
        }
      },
      onError: () => {
        toast.error(t("toast_bulk_failed"));
      },
    });
  }

  return (
    <div data-testid="manual-review-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.operations")}</span>
            <span className="text-[var(--text-subtle)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("nav:item.manual_review")}</span>
          </>
        }
        title={t("title")}
        subtitle={t("subtitle")}
        actions={
          canWrite ? (
            <Button
              variant="default"
              size="sm"
              disabled={bulkDisabled}
              onClick={handleBulkRetry}
              data-testid="bulk-retry-btn"
            >
              {t("btn_retry_all")}
            </Button>
          ) : undefined
        }
      />

      {isError ? (
        <div
          className="rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] py-16 text-center text-sm text-[var(--text-secondary)] shadow-[var(--sh-1)]"
          data-testid="manual-review-error"
        >
          {t("error")}
        </div>
      ) : isLoading ? (
        <div className="rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] shadow-[var(--sh-1)]">
          <ReviewQueueSkeleton />
        </div>
      ) : !rows || rows.length === 0 ? (
        <div
          className="rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] py-16 text-center shadow-[var(--sh-1)]"
          data-testid="manual-review-empty"
        >
          <p className="text-sm font-medium text-[var(--text-secondary)]">{t("empty")}</p>
        </div>
      ) : (
        <ReviewQueueTable rows={rows} canResolve={canRowResolve} />
      )}
    </div>
  );
}
